import {
  BoostingStep,
  OnboardingStep,
  useActionSteps,
} from "UserCrowd/hooks/useActionSteps"
import React, { createContext, useEffect, useState } from "react"
import { Phase } from "./constants"

type ActionsDrawerContextType = {
  showDrawer: boolean
  currentPhase: Phase
  onboardingSteps: OnboardingStep[]
  boostingSteps: BoostingStep[]
  handleOnboardingDismiss: () => void
  setIsAwaitingOnboardingCompletion: (awaiting: boolean) => void
  linkedInVerificationModalOpen: boolean
  linkedinOptOutModalOpen: boolean
  setLinkedInVerificationModalOpen: (open: boolean) => void
  setLinkedInOptOutModalOpen: (open: boolean) => void
}

const ActionsDrawerContext = createContext<ActionsDrawerContextType | null>(
  null
)

export const useActionsDrawerContext = () => {
  const context = React.useContext(ActionsDrawerContext)

  if (context === null) {
    throw new Error(
      "useActionsDrawerContext must be used inside the ActionsDrawerContextProvider"
    )
  }

  return context
}

export const ActionsDrawerContextProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { onboardingSteps, boostingSteps, currentPhase } = useActionSteps()

  const [linkedInVerificationModalOpen, setLinkedInVerificationModalOpen] =
    useState(false)
  const [linkedinOptOutModalOpen, setLinkedInOptOutModalOpen] = useState(false)

  // isAwaitingOnboardingCompletion is set to true when the current phase is boosting and the previous phase
  // was onboarding. This happens when a user completes their practice test.
  // It is used to determine if the user should be held in the onboarding phase until they click a CTA to
  // continue.
  const [isAwaitingOnboardingCompletion, setIsAwaitingOnboardingCompletion] =
    useState(false)

  // If the user is in the boosting phase, this is used to signal the sidebar is dismissed.
  const [isDismissed, setIsDismissed] = useState(true)

  const handleOnboardingDismiss = () => setIsAwaitingOnboardingCompletion(false)

  const showDrawer = !isDismissed

  useEffect(() => {
    // Store initial phase in session storage if it's onboarding
    if (currentPhase === "onboarding") {
      sessionStorage.setItem("initialPhase", "onboarding")
    }

    // Check if we're transitioning from onboarding to boosting
    const initialPhase = sessionStorage.getItem("initialPhase")
    if (initialPhase === "onboarding" && currentPhase === "boosting") {
      setIsAwaitingOnboardingCompletion(true)
      // Clear the session storage after transition is handled
      sessionStorage.removeItem("initialPhase")
    }
  }, [currentPhase])

  // This controls the logic as to whether the sidebar is shown or not.
  useEffect(() => {
    // If the current phase is boosting...
    if (currentPhase === "boosting") {
      // If there are any incomplete steps, the sidebar should be shown.
      if (
        boostingSteps.filter((step) => step.status === "incomplete").length > 0
      ) {
        setIsDismissed(false)
      }
      // If there are no incomplete steps, the sidebar should be dismissed.
      else {
        setIsDismissed(true)
      }
    }
    // If the current phase is onboarding...
    else {
      // The sidebar is always shown in the onboarding phase.
      setIsDismissed(false)
    }
  }, [currentPhase, boostingSteps, onboardingSteps])

  return (
    <ActionsDrawerContext.Provider
      value={{
        showDrawer,
        currentPhase: isAwaitingOnboardingCompletion
          ? "onboarding"
          : currentPhase,
        onboardingSteps,
        boostingSteps,
        handleOnboardingDismiss,
        setIsAwaitingOnboardingCompletion,
        linkedInVerificationModalOpen,
        linkedinOptOutModalOpen,
        setLinkedInVerificationModalOpen,
        setLinkedInOptOutModalOpen,
      }}
    >
      {children}
    </ActionsDrawerContext.Provider>
  )
}
